import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { useReportData } from "../../hooks/useReportData";
import {
  reportsExtendedApi,
  type BudgetVarianceResponse,
  type BudgetVarianceItem,
  type BudgetVarianceParams,
} from "../../services/reports-extended.api";
import { Finance } from "@/components/icons";
import styles from "./BudgetReportPage.module.scss";

const PAGE_SIZE = 20;

const formatCurrency = (v: number) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  }).format(v);

const BudgetReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [approvedOnly, setApprovedOnly] = useState<boolean>(false);
  const [page, setPage] = useState(1);

  const params: BudgetVarianceParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      search: filter.search?.trim() || undefined,
      approvedOnly,
    }),
    [filter, approvedOnly, page],
  );

  const { data, isLoading, error } = useReportData<
    BudgetVarianceResponse,
    BudgetVarianceParams
  >({
    fetcher: (p, signal) => reportsExtendedApi.getBudgetVariance(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  const topByBudget = [...items]
    .sort((a, b) => b.plannedBudget - a.plannedBudget)
    .slice(0, 10);

  const columns: TableColumn<BudgetVarianceItem>[] = [
    {
      header: t("reports.budget.headerTraining"),
      accessor: (row) => (
        <span className={styles.titleCell}>
          {row.title}
          {row.isBudgetApproved && (
            <span className={styles.approvedBadge}>{t("reports.budget.approved")}</span>
          )}
        </span>
      ),
    },
    {
      header: "PLANLANAN",
      accessor: (row) => formatCurrency(row.plannedBudget),
    },
    {
      header: t("reports.budget.headerActual"),
      accessor: (row) => formatCurrency(row.actualCost),
    },
    {
      header: "KAYIT",
      accessor: (row) => row.enrollmentCount,
    },
    {
      header: "TAMAMLAYAN",
      accessor: (row) => row.completedCount,
    },
    {
      header: t("reports.budget.headerPerPerson"),
      accessor: (row) => formatCurrency(row.costPerParticipant),
    },
    {
      header: "SAPMA",
      accessor: (row) => (
        <span
          className={
            row.variancePercent > 5
              ? styles.varianceBad
              : row.variancePercent < -5
                ? styles.varianceGood
                : styles.varianceNeutral
          }
        >
          {row.variancePercent >= 0 ? "+" : ""}
          {row.variancePercent.toFixed(1)}%
        </span>
      ),
    },
  ];

  return (
    <ReportDetailLayout
      title={t("reports.budget.title")}
      categoryLabel={t("reportCatalog.categories.financial.title")}
      description={t("reports.budget.description")}
      exportSlot={
        <ReportExportButtons
          reportType="BUDGET_VARIANCE"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="butce-gerceklesme"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          showDepartment={false}
          extras={
            <label className={styles.approvedToggle}>
              <input
                type="checkbox"
                checked={approvedOnly}
                onChange={(e) => {
                  setApprovedOnly(e.target.checked);
                  setPage(1);
                }}
              />
              <span>{t("reports.budget.onlyApproved")}</span>
            </label>
          }
        />
      }
    >
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title="Toplam Planlanan"
          value={summary ? formatCurrency(summary.totalPlanned) : "—"}
          icon={<Finance width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.budget.totalActual")}
          value={summary ? formatCurrency(summary.totalActual) : "—"}
          icon={<Finance width={18} height={18} />}
          accent="success"
        />
        <ReportKPICard
          title="Genel Sapma"
          value={
            summary
              ? `${summary.overallVariancePercent >= 0 ? "+" : ""}${summary.overallVariancePercent.toFixed(1)}%`
              : "—"
          }
          icon={<Finance width={18} height={18} />}
          accent={
            summary && summary.overallVariancePercent > 5
              ? "danger"
              : summary && summary.overallVariancePercent < -5
                ? "success"
                : "warning"
          }
        />
        <ReportKPICard
          title={t("reports.budget.trainingCount")}
          value={summary?.educationCount ?? 0}
          icon={<Finance width={18} height={18} />}
        />
      </div>

      <ReportChart
        title={t("reports.budget.topBudgeted")}
        subtitle={t("reports.budget.subtitle")}
        type="bar"
        isLoading={isLoading}
        isEmpty={topByBudget.length === 0}
        series={[
          {
            name: "Planlanan",
            data: topByBudget.map((i) => i.plannedBudget),
          },
          {
            name: t("reports.budget.actual"),
            data: topByBudget.map((i) => i.actualCost),
          },
        ]}
        options={{
          xaxis: { categories: topByBudget.map((i) => i.title) },
          colors: ["#4f46e5", "#10b981"],
          yaxis: {
            labels: {
              formatter: (val: number) => formatCurrency(val),
            },
          },
        }}
      />

      <Table
        columns={columns}
        data={items}
        isLoading={isLoading}
        getRowId={(row) => row.educationID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error ? t("reports.budget.noData") : t("reports.budget.noBudgeted")
        }
      />
    </ReportDetailLayout>
  );
};

export default BudgetReportPage;
